import { useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [store, setStore] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email,
        shopify_store: store || null,
        source: "landing",
      });
      setSuccess(true);
      trackEvent("waitlist_signup", {
        fb_event_type: "Lead",
        email_provided: true,
        store_provided: Boolean(store),
      });
      toast.success(res.data?.message || "You're on the list.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="waitlist-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 radial-glow pointer-events-none" />
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 80%, rgba(212,255,0,0.15), transparent 40%), radial-gradient(circle at 80% 20%, rgba(236,72,153,0.08), transparent 40%)",
        }}
      />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-[11px] tracking-[0.3em] uppercase text-[#D4FF00] font-semibold">
          Waitlist · limited early access
        </p>
        <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl mt-4">
          Stop guessing <br />
          who your <br />
          <span className="outline-text">creators are.</span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#A1A1AA] max-w-xl mx-auto">
          Join 2,400+ e-commerce brands getting early access. We'll reach out the day your match quota opens up.
        </p>

        {success ? (
          <div
            data-testid="waitlist-success"
            className="mt-12 inline-flex items-center gap-3 px-6 py-5 border border-[#D4FF00] rounded-sm bg-[#D4FF00]/10"
          >
            <CheckCircle2 className="w-5 h-5 text-[#D4FF00]" />
            <span className="font-display uppercase text-sm tracking-wider">
              You're on the list. Watch your inbox.
            </span>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="waitlist-form"
            className="mt-12 max-w-2xl mx-auto flex flex-col gap-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                data-testid="waitlist-email-input"
                type="email"
                required
                placeholder="you@store.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="sm:col-span-2 h-14 rounded-sm bg-white/5 border-white/10 text-white placeholder:text-white/40 text-base focus:border-[#D4FF00] focus-visible:ring-0"
              />
              <Input
                data-testid="waitlist-store-input"
                type="text"
                placeholder="store.myshopify.com"
                value={store}
                onChange={(e) => setStore(e.target.value)}
                className="h-14 rounded-sm bg-white/5 border-white/10 text-white placeholder:text-white/40 text-base focus:border-[#D4FF00] focus-visible:ring-0"
              />
            </div>
            <Button
              data-testid="waitlist-submit-btn"
              type="submit"
              disabled={loading}
              className="btn-lime rounded-sm h-14 w-full text-sm disabled:opacity-70 hover:text-black"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting
                </>
              ) : (
                <>
                  Join the waitlist
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2.5} />
                </>
              )}
            </Button>
            <p className="text-[11px] text-[#A1A1AA] mt-2">
              We send one email when your seat opens. No spam, no list-sharing.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
